import { SectionCard } from "@/components/canvas/SectionCard";
import { ShapeCard, ShapeGlyph } from "@/components/canvas/ShapeCard";
import { ShapePanel } from "@/components/canvas/ShapePanel";
import { StickerInstanceCard } from "@/components/canvas/StickerInstanceCard";
import { StickerPanel } from "@/components/canvas/StickerPanel";
import { StickyNoteCard } from "@/components/canvas/StickyNoteCard";
import { TextBoxCard } from "@/components/canvas/TextBoxCard";
import { ToolButton } from "@/components/canvas/ToolButton";
import {
  clampVp,
  DAY_LABELS,
  MAJOR,
  MAX_SCALE,
  MIN_SCALE,
  MINOR,
  NOTE_BODY_H,
  NOTE_COLORS,
  NOTE_HEADER_H,
  NOTE_W,
  SECTION_H,
  SECTION_PART_W,
  SECTION_PARTS, SECTION_TITLE_H,
  SHAPE_DEFAULT_COLOR,
  shapeDefaultSize,
  STICKER_SIZE,
} from "@/constants";
import type { ClipboardData, ClipboardItem, Note, Section, Shape, ShapeKind, StickerDef, StickerInstance, TextBox, Tool, Viewport } from "@/types";
import {
  Columns3,
  Image as ImageIcon,
  Inbox as InboxIcon,
  MousePointer2,
  Shapes as ShapesIcon,
  StickyNote as StickyNoteIcon,
  Type as TypeIcon,
  X as XIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import "./index.css";

function toLocalDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getWeekDays(weekOffset: number) {
  const today = new Date();
  const dow = today.getDay();
  const sun = new Date(today);
  sun.setDate(today.getDate() - dow + weekOffset * 7);
  const todayStr = toLocalDateStr(today);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sun);
    d.setDate(sun.getDate() + i);
    const dateStr = toLocalDateStr(d);
    return { label: DAY_LABELS[i], date: d.getDate(), dateStr, isToday: dateStr === todayStr };
  });
}
function isNoteCompletelyInSection(note: Note, section: Section): boolean {
  return (
    note.x >= section.x &&
    note.x + NOTE_W <= section.x + section.w &&
    note.y >= section.y &&
    note.y + NOTE_HEADER_H + NOTE_BODY_H <= section.y + SECTION_TITLE_H + section.h
  );
}

function isStickerOnNote(sticker: StickerInstance, note: Note): boolean {
  const cx = sticker.x + sticker.w / 2;
  const cy = sticker.y + sticker.h / 2;
  return (
    cx >= note.x &&
    cx <= note.x + NOTE_W &&
    cy >= note.y &&
    cy <= note.y + NOTE_HEADER_H + NOTE_BODY_H
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

function vpKey(date: string) { return `vp-${date}`; }
function loadVp(date: string): Viewport {
  try {
    const s = localStorage.getItem(vpKey(date));
    if (s) return JSON.parse(s);
  } catch {}
  return { x: 0, y: 0, scale: 1 };
}

export function App({ user, onSignOut }: { user: { name: string; email: string }; onSignOut: () => void }) {
  const [weekOffset, setWeekOffset] = useState(() => {
    try { const s = localStorage.getItem("weekOffset"); return s ? parseInt(s, 10) : 0; } catch { return 0; }
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    try { return localStorage.getItem("selectedDate") ?? toLocalDateStr(new Date()); } catch { return toLocalDateStr(new Date()); }
  });
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxNotes, setInboxNotes] = useState<Note[]>([]);
  const [inboxStickers, setInboxStickers] = useState<StickerInstance[]>([]);
  const floatingBarRef = useRef<HTMLDivElement>(null);
  const days = getWeekDays(weekOffset);
  const [vp, setVp] = useState<Viewport>(() => loadVp(
    (() => { try { return localStorage.getItem("selectedDate") ?? toLocalDateStr(new Date()); } catch { return toLocalDateStr(new Date()); } })()
  ));
  const [tool, setTool] = useState<Tool>("pan");
  const [notes, setNotes] = useState<Note[]>([]);
  const [stickerDefs, setStickerDefs] = useState<StickerDef[]>([]);
  const [stickerInstances, setStickerInstances] = useState<StickerInstance[]>([]);
  const [stickerPanelOpen, setStickerPanelOpen] = useState(false);
  const [activeStickerDef, setActiveStickerDef] = useState<StickerDef | null>(null);
  const [textBoxes, setTextBoxes] = useState<TextBox[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [shapePanelOpen, setShapePanelOpen] = useState(false);
  const [activeShapeKind, setActiveShapeKind] = useState<ShapeKind | null>(null);
  const [ghostShapePos, setGhostShapePos] = useState<{ x: number; y: number } | null>(null);
  const [selectedShapeColor, setSelectedShapeColor] = useState<string>(SHAPE_DEFAULT_COLOR);
  const [selectedNoteColor, setSelectedNoteColor] = useState<string>(NOTE_COLORS[0].body);
  const [newNoteId, setNewNoteId] = useState<string | null>(null);
  const [newTextBoxId, setNewTextBoxId] = useState<string | null>(null);
  const [clipboard, setClipboard] = useState<ClipboardData | null>(null);
  const [ghostNotePos, setGhostNotePos] = useState<{ x: number; y: number } | null>(null);
  const [ghostStickerPos, setGhostStickerPos] = useState<{ x: number; y: number } | null>(null);
  const [ghostSectionPos, setGhostSectionPos] = useState<{ x: number; y: number } | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectionRect, setSelectionRect] = useState<{ sx: number; sy: number; ex: number; ey: number } | null>(null);
  const [groupDragging, setGroupDragging] = useState(false);
  const selectionRectRef = useRef<{ sx: number; sy: number; ex: number; ey: number } | null>(null);
  const groupDragRef = useRef<{
    startX: number; startY: number; currentDx: number; currentDy: number;
    startPositions: Array<{ id: string; type: "note" | "sticker" | "text" | "section" | "shape"; x: number; y: number }>;
  } | null>(null);
  const lastMouse = useRef({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(1) as React.RefObject<number>;
  (scaleRef as React.MutableRefObject<number>).current = vp.scale;
  const contentTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const stickerInstancesRef = useRef(stickerInstances);
  stickerInstancesRef.current = stickerInstances;
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const sectionDragRef = useRef<{
    sectionId: string;
    sectionStartX: number;
    sectionStartY: number;
    notes: Array<{ id: string; startX: number; startY: number }>;
  } | null>(null);
  const noteDragRef = useRef<{
    noteId: string;
    noteStartX: number;
    noteStartY: number;
    stickers: Array<{ id: string; startX: number; startY: number }>;
  } | null>(null);

  useEffect(() => {
    fetch(`/api/sticky-notes?date=${selectedDate}&inbox=false`)
      .then((r) => r.json()).then(setNotes).catch(console.error);
  }, [selectedDate]);

  useEffect(() => {
    fetch(`/api/sticky-notes?date=${selectedDate}&inbox=true`)
      .then((r) => r.json()).then(setInboxNotes).catch(console.error);
  }, [selectedDate]);

  useEffect(() => {
    fetch("/api/stickers").then((r) => r.json()).then(setStickerDefs).catch(console.error);
  }, []);

  useEffect(() => {
    fetch(`/api/sticker-instances?date=${selectedDate}&inbox=false`)
      .then((r) => r.json()).then(setStickerInstances).catch(console.error);
  }, [selectedDate]);

  useEffect(() => {
    fetch(`/api/sticker-instances?date=${selectedDate}&inbox=true`)
      .then((r) => r.json()).then(setInboxStickers).catch(console.error);
  }, [selectedDate]);

  useEffect(() => {
    fetch(`/api/text-boxes?date=${selectedDate}`)
      .then((r) => r.json()).then(setTextBoxes).catch(console.error);
  }, [selectedDate]);

  useEffect(() => {
    fetch(`/api/sections?date=${selectedDate}`)
      .then((r) => r.json()).then(setSections).catch(console.error);
  }, [selectedDate]);

  useEffect(() => {
    fetch(`/api/shapes?date=${selectedDate}`)
      .then((r) => r.json()).then(setShapes).catch(console.error);
  }, [selectedDate]);

  useEffect(() => {
    if (!dateDropdownOpen && !profileOpen) return;
    const close = (e: MouseEvent) => {
      if (!floatingBarRef.current?.contains(e.target as Node)) {
        setDateDropdownOpen(false);
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [dateDropdownOpen, profileOpen]);

  useEffect(() => {
    try { localStorage.setItem("weekOffset", String(weekOffset)); } catch {}
  }, [weekOffset]);

  useEffect(() => {
    try { localStorage.setItem("selectedDate", selectedDate); } catch {}
  }, [selectedDate]);

  useEffect(() => {
    try { localStorage.setItem(vpKey(selectedDate), JSON.stringify(vp)); } catch {}
  }, [vp, selectedDate]);

  useEffect(() => {
    const PASTE_OFFSET = 20;
    const onKey = async (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "TEXTAREA" || tag === "INPUT") return;
      if ((e.key === "v" || e.key === "V") && !e.metaKey && !e.ctrlKey) { setTool("pan"); setStickerPanelOpen(false); setActiveStickerDef(null); }
      if (e.key === "s" || e.key === "S") { setTool("sticky"); setStickerPanelOpen(false); }
      if (e.key === "t" || e.key === "T") { setTool("text"); setStickerPanelOpen(false); }
      if (e.key === "g" || e.key === "G") { setTool("section"); setStickerPanelOpen(false); setActiveStickerDef(null); }
      if (e.key === "Escape") { setTool("pan"); setSelectedIds([]); setStickerPanelOpen(false); setActiveStickerDef(null); setGhostSectionPos(null); setShapePanelOpen(false); setActiveShapeKind(null); setGhostShapePos(null); }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedIds.length > 0) {
        for (const id of selectedIds) {
          if (notes.some((n) => n._id === id)) handleDeleteNote(id);
          else if (textBoxes.some((b) => b._id === id)) handleDeleteTextBox(id);
          else if (sections.some((s) => s._id === id)) handleDeleteSection(id);
          else if (shapes.some((s) => s._id === id)) handleDeleteShape(id);
          else handleDeleteStickerInstance(id);
        }
        setSelectedIds([]);
      }
      if ((e.key === "c" || e.key === "C") && (e.metaKey || e.ctrlKey) && selectedIds.length > 0) {
        e.preventDefault();
        const items: ClipboardItem[] = [];
        for (const id of selectedIds) {
          const note = notes.find((n) => n._id === id);
          const inst = stickerInstances.find((i) => i._id === id);
          const box = textBoxes.find((b) => b._id === id);
          const shape = shapes.find((s) => s._id === id);
          if (note) items.push({ type: "note", data: note });
          else if (inst) items.push({ type: "sticker", data: inst });
          else if (box) items.push({ type: "textBox", data: box });
          else if (shape) items.push({ type: "shape", data: shape });
        }
        if (items.length > 0) setClipboard(items);
      }
      if ((e.key === "x" || e.key === "X") && (e.metaKey || e.ctrlKey) && selectedIds.length > 0) {
        e.preventDefault();
        const items: ClipboardItem[] = [];
        for (const id of selectedIds) {
          const note = notes.find((n) => n._id === id);
          const inst = stickerInstances.find((i) => i._id === id);
          const box = textBoxes.find((b) => b._id === id);
          const shape = shapes.find((s) => s._id === id);
          if (note) items.push({ type: "note", data: note });
          else if (inst) items.push({ type: "sticker", data: inst });
          else if (box) items.push({ type: "textBox", data: box });
          else if (shape) items.push({ type: "shape", data: shape });
        }
        if (items.length > 0) {
          setClipboard(items);
          for (const id of selectedIds) {
            if (notes.some((n) => n._id === id)) handleDeleteNote(id);
            else if (textBoxes.some((b) => b._id === id)) handleDeleteTextBox(id);
            else if (shapes.some((s) => s._id === id)) handleDeleteShape(id);
            else handleDeleteStickerInstance(id);
          }
          setSelectedIds([]);
        }
      }
      if ((e.key === "v" || e.key === "V") && (e.metaKey || e.ctrlKey) && clipboard && clipboard.length > 0) {
        e.preventDefault();
        const newIds: string[] = [];
        const newClipboard: ClipboardItem[] = [];
        for (const item of clipboard) {
          if (item.type === "note") {
            const d = item.data;
            try {
              const res = await fetch("/api/sticky-notes", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ x: d.x + PASTE_OFFSET, y: d.y + PASTE_OFFSET, content: d.content, color: d.color, date: selectedDate }),
              });
              const note = await res.json();
              setNotes((prev) => [...prev, note]);
              newIds.push(note._id);
              newClipboard.push({ type: "note", data: { ...d, x: d.x + PASTE_OFFSET, y: d.y + PASTE_OFFSET } });
            } catch (err) { console.error(err); }
          } else if (item.type === "sticker") {
            const d = item.data;
            try {
              const res = await fetch("/api/sticker-instances", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ stickerId: d.stickerId, x: d.x + PASTE_OFFSET, y: d.y + PASTE_OFFSET, w: d.w, h: d.h, date: selectedDate }),
              });
              const inst = await res.json();
              setStickerInstances((prev) => [...prev, inst]);
              newIds.push(inst._id);
              newClipboard.push({ type: "sticker", data: { ...d, x: d.x + PASTE_OFFSET, y: d.y + PASTE_OFFSET } });
            } catch (err) { console.error(err); }
          } else if (item.type === "textBox") {
            const d = item.data;
            try {
              const res = await fetch("/api/text-boxes", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ x: d.x + PASTE_OFFSET, y: d.y + PASTE_OFFSET, content: d.content, fontSize: d.fontSize, date: selectedDate }),
              });
              const box = await res.json();
              setTextBoxes((prev) => [...prev, box]);
              newIds.push(box._id);
              newClipboard.push({ type: "textBox", data: { ...d, x: d.x + PASTE_OFFSET, y: d.y + PASTE_OFFSET } });
            } catch (err) { console.error(err); }
          } else if (item.type === "shape") {
            const d = item.data;
            try {
              const res = await fetch("/api/shapes", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ x: d.x + PASTE_OFFSET, y: d.y + PASTE_OFFSET, w: d.w, h: d.h, kind: d.kind, color: d.color, date: selectedDate }),
              });
              const shape = await res.json();
              setShapes((prev) => [...prev, shape]);
              newIds.push(shape._id);
              newClipboard.push({ type: "shape", data: { ...d, x: d.x + PASTE_OFFSET, y: d.y + PASTE_OFFSET } });
            } catch (err) { console.error(err); }
          }
        }
        if (newIds.length > 0) setSelectedIds(newIds);
        if (newClipboard.length > 0) setClipboard(newClipboard);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIds, notes, textBoxes, sections, stickerInstances, shapes, clipboard, selectedDate]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.ctrlKey) {
        const rect = el.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
        setVp((v) => {
          const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
          const ratio = newScale / v.scale;
          return clampVp({ scale: newScale, x: cx - (cx - v.x) * ratio, y: cy - (cy - v.y) * ratio });
        });
      } else {
        setVp((v) => clampVp({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onCanvasDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setStickerPanelOpen(false);
    setShapePanelOpen(false);
    if (tool === "pan") {
      setSelectedIds([]);
      const rect = canvasRef.current!.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      selectionRectRef.current = { sx, sy, ex: sx, ey: sy };
      setSelectionRect({ sx, sy, ex: sx, ey: sy });
    }
  };

  const onCanvasMove = (e: React.MouseEvent) => {
    if (tool === "sticky") {
      const rect = canvasRef.current!.getBoundingClientRect();
      const wx = (e.clientX - rect.left - vp.x) / vp.scale - NOTE_W / 2;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale - NOTE_BODY_H / 2;
      setGhostNotePos({ x: wx, y: wy });
    }
    if (tool === "sticker" && activeStickerDef) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const wx = (e.clientX - rect.left - vp.x) / vp.scale - STICKER_SIZE / 2;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale - STICKER_SIZE / 2;
      setGhostStickerPos({ x: wx, y: wy });
    }
    if (tool === "section") {
      const rect = canvasRef.current!.getBoundingClientRect();
      const sectionW = SECTION_PART_W * SECTION_PARTS;
      const wx = (e.clientX - rect.left - vp.x) / vp.scale - sectionW / 2;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale - (SECTION_H + SECTION_TITLE_H) / 2;
      setGhostSectionPos({ x: wx, y: wy });
    }
    if (tool === "shape" && activeShapeKind) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const { w, h } = shapeDefaultSize(activeShapeKind);
      const wx = (e.clientX - rect.left - vp.x) / vp.scale - w / 2;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale - h / 2;
      setGhostShapePos({ x: wx, y: wy });
    }
    if (selectionRectRef.current) {
      const rect = canvasRef.current!.getBoundingClientRect();
      selectionRectRef.current = { ...selectionRectRef.current, ex: e.clientX - rect.left, ey: e.clientY - rect.top };
      setSelectionRect({ ...selectionRectRef.current });
    }
  };

  const onCanvasUp = async (e: React.MouseEvent) => {
    if (selectionRectRef.current) {
      const { sx, sy, ex, ey } = selectionRectRef.current;
      selectionRectRef.current = null;
      setSelectionRect(null);
      if (Math.abs(ex - sx) > 4 || Math.abs(ey - sy) > 4) {
        const x1 = (Math.min(sx, ex) - vp.x) / vp.scale;
        const y1 = (Math.min(sy, ey) - vp.y) / vp.scale;
        const x2 = (Math.max(sx, ex) - vp.x) / vp.scale;
        const y2 = (Math.max(sy, ey) - vp.y) / vp.scale;
        const ids: string[] = [];
        sections.forEach((s) => {
          if (s.x < x2 && s.x + s.w > x1 && s.y < y2 && s.y + s.h + SECTION_TITLE_H > y1) ids.push(s._id);
        });
        notes.forEach((n) => {
          if (n.x < x2 && n.x + NOTE_W > x1 && n.y < y2 && n.y + NOTE_BODY_H + NOTE_HEADER_H > y1) ids.push(n._id);
        });
        stickerInstances.forEach((s) => {
          if (s.x < x2 && s.x + s.w > x1 && s.y < y2 && s.y + s.h > y1) ids.push(s._id);
        });
        textBoxes.forEach((t) => {
          if (t.x < x2 && t.x + 300 > x1 && t.y < y2 && t.y + 80 > y1) ids.push(t._id);
        });
        shapes.forEach((s) => {
          if (s.x < x2 && s.x + s.w > x1 && s.y < y2 && s.y + s.h > y1) ids.push(s._id);
        });
        setSelectedIds(ids);
      }
      return;
    }
    if (tool === "sticky") {
      const rect = canvasRef.current!.getBoundingClientRect();
      const wx = (e.clientX - rect.left - vp.x) / vp.scale - NOTE_W / 2;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale - NOTE_BODY_H / 2;
      try {
        const res = await fetch("/api/sticky-notes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ x: wx, y: wy, content: "", color: selectedNoteColor, date: selectedDate }),
        });
        const note = await res.json();
        setNotes((prev) => [...prev, note]);
        setNewNoteId(note._id);
      } catch (err) { console.error(err); }
      setTool("pan");
      setGhostNotePos(null);
    }
    if (tool === "text") {
      const rect = canvasRef.current!.getBoundingClientRect();
      const wx = (e.clientX - rect.left - vp.x) / vp.scale;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale;
      try {
        const res = await fetch("/api/text-boxes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ x: wx, y: wy, content: "", fontSize: 18, date: selectedDate }),
        });
        const box = await res.json();
        setTextBoxes((prev) => [...prev, box]);
        setNewTextBoxId(box._id);
      } catch (err) { console.error(err); }
      setTool("pan");
    }
    if (tool === "section") {
      const rect = canvasRef.current!.getBoundingClientRect();
      const sectionW = SECTION_PART_W * SECTION_PARTS;
      const wx = (e.clientX - rect.left - vp.x) / vp.scale - sectionW / 2;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale - (SECTION_H + SECTION_TITLE_H) / 2;
      try {
        const res = await fetch("/api/sections", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ x: wx, y: wy, title: "", parts: SECTION_PARTS, w: sectionW, h: SECTION_H, date: selectedDate }),
        });
        const section = await res.json();
        setSections((prev) => [...prev, section]);
      } catch (err) { console.error(err); }
      setTool("pan");
      setGhostSectionPos(null);
    }
    if (tool === "sticker" && activeStickerDef) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const wx = (e.clientX - rect.left - vp.x) / vp.scale - STICKER_SIZE / 2;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale - STICKER_SIZE / 2;
      try {
        const res = await fetch("/api/sticker-instances", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ stickerId: activeStickerDef._id, x: wx, y: wy, w: STICKER_SIZE, h: STICKER_SIZE, date: selectedDate }),
        });
        const inst = await res.json();
        setStickerInstances((prev) => [...prev, inst]);
      } catch (err) { console.error(err); }
      setTool("pan");
      setActiveStickerDef(null);
      setGhostStickerPos(null);
    }
    if (tool === "shape" && activeShapeKind) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const { w, h } = shapeDefaultSize(activeShapeKind);
      const wx = (e.clientX - rect.left - vp.x) / vp.scale - w / 2;
      const wy = (e.clientY - rect.top - vp.y) / vp.scale - h / 2;
      try {
        const res = await fetch("/api/shapes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ x: wx, y: wy, w, h, kind: activeShapeKind, color: selectedShapeColor, date: selectedDate }),
        });
        const shape = await res.json();
        setShapes((prev) => [...prev, shape]);
        setSelectedIds([shape._id]);
      } catch (err) { console.error(err); }
      setTool("pan");
      setActiveShapeKind(null);
      setGhostShapePos(null);
    }
  };

  const handleMoveNote = useCallback((id: string, x: number, y: number) => {
    setNotes((prev) => prev.map((n) => (n._id === id ? { ...n, x, y } : n)));
    if (noteDragRef.current?.noteId === id && noteDragRef.current.stickers.length > 0) {
      const dx = x - noteDragRef.current.noteStartX;
      const dy = y - noteDragRef.current.noteStartY;
      const stickerUpdates = new Map(
        noteDragRef.current.stickers.map(({ id: sid, startX, startY }) => [sid, { x: startX + dx, y: startY + dy }])
      );
      setStickerInstances((prev) => prev.map((inst) => {
        const upd = stickerUpdates.get(inst._id);
        return upd ? { ...inst, ...upd } : inst;
      }));
    }
  }, []);

  const handleSaveNotePosition = useCallback(async (id: string, x: number, y: number) => {
    await fetch(`/api/sticky-notes/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ x, y }),
    });
    if (noteDragRef.current?.noteId === id) {
      const { noteStartX, noteStartY, stickers: dragStickers } = noteDragRef.current;
      noteDragRef.current = null;
      if (dragStickers.length > 0) {
        const dx = x - noteStartX;
        const dy = y - noteStartY;
        await Promise.all(dragStickers.map(({ id: sid, startX, startY }) =>
          fetch(`/api/sticker-instances/${sid}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ x: startX + dx, y: startY + dy }),
          })
        ));
      }
    }
  }, []);

  const handleContentChange = useCallback((id: string, content: string) => {
    setNotes((prev) => prev.map((n) => (n._id === id ? { ...n, content } : n)));
    const timers = contentTimers.current;
    if (timers.has(id)) clearTimeout(timers.get(id)!);
    timers.set(id, setTimeout(async () => {
      await fetch(`/api/sticky-notes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      timers.delete(id);
    }, 600));
  }, []);

  const handleColorChange = useCallback(async (id: string, color: string) => {
    setNotes((prev) => prev.map((n) => n._id === id ? { ...n, color } : n));
    setSelectedNoteColor(color);
    await fetch(`/api/sticky-notes/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ color }),
    });
  }, []);

  const handleDeleteNote = useCallback(async (id: string) => {
    setNotes((prev) => prev.filter((n) => n._id !== id));
    await fetch(`/api/sticky-notes/${id}`, { method: "DELETE" });
  }, []);

  const handleInboxDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const rect = canvasRef.current!.getBoundingClientRect();
    const noteId = e.dataTransfer.getData("text/inbox-note");
    if (noteId) {
      const note = inboxNotes.find((n) => n._id === noteId);
      if (!note) return;
      const x = (e.clientX - rect.left - vp.x) / vp.scale - NOTE_W / 2;
      const y = (e.clientY - rect.top - vp.y) / vp.scale - NOTE_BODY_H / 2;
      setInboxNotes((prev) => prev.filter((n) => n._id !== noteId));
      setNotes((prev) => [...prev, { ...note, x, y, inbox: false }]);
      await fetch(`/api/sticky-notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ x, y, inbox: false }),
      });
      return;
    }
    const stickerId = e.dataTransfer.getData("text/inbox-sticker");
    if (stickerId) {
      const inst = inboxStickers.find((s) => s._id === stickerId);
      if (!inst) return;
      const x = (e.clientX - rect.left - vp.x) / vp.scale - inst.w / 2;
      const y = (e.clientY - rect.top - vp.y) / vp.scale - inst.h / 2;
      setInboxStickers((prev) => prev.filter((s) => s._id !== stickerId));
      setStickerInstances((prev) => [...prev, { ...inst, x, y, inbox: false }]);
      await fetch(`/api/sticker-instances/${stickerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ x, y, inbox: false }),
      });
    }
  }, [inboxNotes, inboxStickers, vp]);

  const handleMoveTextBox = useCallback((id: string, x: number, y: number) => {
    setTextBoxes((prev) => prev.map((b) => (b._id === id ? { ...b, x, y } : b)));
  }, []);

  const handleSaveTextBoxPosition = useCallback(async (id: string, x: number, y: number) => {
    await fetch(`/api/text-boxes/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ x, y }),
    });
  }, []);

  const handleTextBoxContentChange = useCallback((id: string, content: string) => {
    setTextBoxes((prev) => prev.map((b) => (b._id === id ? { ...b, content } : b)));
    const timers = contentTimers.current;
    if (timers.has(id)) clearTimeout(timers.get(id)!);
    timers.set(id, setTimeout(async () => {
      await fetch(`/api/text-boxes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      timers.delete(id);
    }, 600));
  }, []);

  const handleDeleteTextBox = useCallback(async (id: string) => {
    setTextBoxes((prev) => prev.filter((b) => b._id !== id));
    await fetch(`/api/text-boxes/${id}`, { method: "DELETE" });
  }, []);

  const handleMoveStickerInstance = useCallback((id: string, x: number, y: number) => {
    setStickerInstances((prev) => prev.map((i) => (i._id === id ? { ...i, x, y } : i)));
  }, []);

  const handleSaveStickerPosition = useCallback(async (id: string, x: number, y: number) => {
    await fetch(`/api/sticker-instances/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ x, y }),
    });
  }, []);

  const handleResizeStickerInstance = useCallback((id: string, w: number, h: number) => {
    setStickerInstances((prev) => prev.map((i) => (i._id === id ? { ...i, w, h } : i)));
  }, []);

  const handleSaveStickerSize = useCallback(async (id: string, w: number, h: number) => {
    await fetch(`/api/sticker-instances/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ w, h }),
    });
  }, []);

  const handleDeleteStickerInstance = useCallback(async (id: string) => {
    setStickerInstances((prev) => prev.filter((i) => i._id !== id));
    await fetch(`/api/sticker-instances/${id}`, { method: "DELETE" });
  }, []);

  const handleMoveSection = useCallback((id: string, x: number, y: number) => {
    setSections((prev) => prev.map((s) => (s._id === id ? { ...s, x, y } : s)));
    if (sectionDragRef.current?.sectionId === id && sectionDragRef.current.notes.length > 0) {
      const dx = x - sectionDragRef.current.sectionStartX;
      const dy = y - sectionDragRef.current.sectionStartY;
      const noteUpdates = new Map(
        sectionDragRef.current.notes.map(({ id: nid, startX, startY }) => [nid, { x: startX + dx, y: startY + dy }])
      );
      setNotes((prev) => prev.map((n) => {
        const upd = noteUpdates.get(n._id);
        return upd ? { ...n, ...upd } : n;
      }));
    }
  }, []);

  const handleSaveSectionPosition = useCallback(async (id: string, x: number, y: number) => {
    await fetch(`/api/sections/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ x, y }),
    });
    if (sectionDragRef.current?.sectionId === id) {
      const { sectionStartX, sectionStartY, notes: dragNotes } = sectionDragRef.current;
      sectionDragRef.current = null;
      if (dragNotes.length > 0) {
        const dx = x - sectionStartX;
        const dy = y - sectionStartY;
        await Promise.all(dragNotes.map(({ id: nid, startX, startY }) =>
          fetch(`/api/sticky-notes/${nid}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ x: startX + dx, y: startY + dy }),
          })
        ));
      }
    }
  }, []);

  const handleSectionTitleChange = useCallback((id: string, title: string) => {
    setSections((prev) => prev.map((s) => (s._id === id ? { ...s, title } : s)));
    const timers = contentTimers.current;
    if (timers.has(id)) clearTimeout(timers.get(id)!);
    timers.set(id, setTimeout(async () => {
      await fetch(`/api/sections/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      timers.delete(id);
    }, 600));
  }, []);

  const handleResizeSection = useCallback((id: string, x: number, y: number, w: number, h: number) => {
    setSections((prev) => prev.map((s) => (s._id === id ? { ...s, x, y, w, h } : s)));
  }, []);

  const handleSaveSectionSize = useCallback(async (id: string, x: number, y: number, w: number, h: number) => {
    await fetch(`/api/sections/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ x, y, w, h }),
    });
  }, []);

  const handleDeleteSection = useCallback(async (id: string) => {
    setSections((prev) => prev.filter((s) => s._id !== id));
    await fetch(`/api/sections/${id}`, { method: "DELETE" });
  }, []);

  const handleMoveShape = useCallback((id: string, x: number, y: number) => {
    setShapes((prev) => prev.map((s) => (s._id === id ? { ...s, x, y } : s)));
  }, []);

  const handleSaveShapePosition = useCallback(async (id: string, x: number, y: number) => {
    await fetch(`/api/shapes/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ x, y }),
    });
  }, []);

  const handleResizeShape = useCallback((id: string, w: number, h: number) => {
    setShapes((prev) => prev.map((s) => (s._id === id ? { ...s, w, h } : s)));
  }, []);

  const handleSaveShapeSize = useCallback(async (id: string, w: number, h: number) => {
    await fetch(`/api/shapes/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ w, h }),
    });
  }, []);

  const handleShapeColorChange = useCallback(async (id: string, color: string) => {
    setShapes((prev) => prev.map((s) => (s._id === id ? { ...s, color } : s)));
    setSelectedShapeColor(color);
    await fetch(`/api/shapes/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ color }),
    });
  }, []);

  const handleDeleteShape = useCallback(async (id: string) => {
    setShapes((prev) => prev.filter((s) => s._id !== id));
    await fetch(`/api/shapes/${id}`, { method: "DELETE" });
  }, []);

  const handleUploadSticker = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch("/api/stickers", { method: "POST", body: formData });
      const def = await res.json();
      setStickerDefs((prev) => [...prev, def]);
    } catch (err) { console.error(err); }
  }, []);

  const handleUploadStickerFromUrl = useCallback(async (url: string) => {
    try {
      const res = await fetch("/api/stickers/from-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) { console.error("Failed to fetch sticker from URL:", await res.text()); return; }
      const def = await res.json();
      setStickerDefs((prev) => [...prev, def]);
    } catch (err) { console.error(err); }
  }, []);

  const handleDeleteStickerDef = useCallback(async (id: string) => {
    setStickerDefs((prev) => prev.filter((s) => s._id !== id));
    setStickerInstances((prev) => prev.filter((i) => i.stickerId !== id));
    await fetch(`/api/stickers/${id}`, { method: "DELETE" });
  }, []);

  const handleSectionDragStart = useCallback((id: string) => {
    const section = sectionsRef.current.find((s) => s._id === id);
    if (!section) return;
    const containedNotes = notesRef.current.filter((n) => isNoteCompletelyInSection(n, section));
    sectionDragRef.current = {
      sectionId: id,
      sectionStartX: section.x,
      sectionStartY: section.y,
      notes: containedNotes.map((n) => ({ id: n._id, startX: n.x, startY: n.y })),
    };
  }, []);

  const handleNoteDragStart = useCallback((id: string) => {
    const note = notesRef.current.find((n) => n._id === id);
    if (!note) return;
    const onNote = stickerInstancesRef.current.filter((inst) => isStickerOnNote(inst, note));
    noteDragRef.current = {
      noteId: id,
      noteStartX: note.x,
      noteStartY: note.y,
      stickers: onNote.map((i) => ({ id: i._id, startX: i.x, startY: i.y })),
    };
  }, []);

  const onGroupPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    sectionDragRef.current = null;
    noteDragRef.current = null;
    const positions: Array<{ id: string; type: "note" | "sticker" | "text" | "section" | "shape"; x: number; y: number }> = [];
    sections.filter((s) => selectedIds.includes(s._id)).forEach((s) => positions.push({ id: s._id, type: "section", x: s.x, y: s.y }));
    notes.filter((n) => selectedIds.includes(n._id)).forEach((n) => positions.push({ id: n._id, type: "note", x: n.x, y: n.y }));
    stickerInstances.filter((s) => selectedIds.includes(s._id)).forEach((s) => positions.push({ id: s._id, type: "sticker", x: s.x, y: s.y }));
    textBoxes.filter((t) => selectedIds.includes(t._id)).forEach((t) => positions.push({ id: t._id, type: "text", x: t.x, y: t.y }));
    shapes.filter((s) => selectedIds.includes(s._id)).forEach((s) => positions.push({ id: s._id, type: "shape", x: s.x, y: s.y }));
    groupDragRef.current = { startX: e.clientX, startY: e.clientY, currentDx: 0, currentDy: 0, startPositions: positions };
    setGroupDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onGroupPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!groupDragRef.current) return;
    const s = scaleRef.current ?? 1;
    const dx = (e.clientX - groupDragRef.current.startX) / s;
    const dy = (e.clientY - groupDragRef.current.startY) / s;
    groupDragRef.current.currentDx = dx;
    groupDragRef.current.currentDy = dy;
    groupDragRef.current.startPositions.forEach(({ id, type, x, y }) => {
      if (type === "note") handleMoveNote(id, x + dx, y + dy);
      else if (type === "sticker") handleMoveStickerInstance(id, x + dx, y + dy);
      else if (type === "text") handleMoveTextBox(id, x + dx, y + dy);
      else if (type === "section") handleMoveSection(id, x + dx, y + dy);
      else if (type === "shape") handleMoveShape(id, x + dx, y + dy);
    });
  };

  const onGroupPointerUp = async () => {
    if (!groupDragRef.current) return;
    const { startPositions, currentDx, currentDy } = groupDragRef.current;
    groupDragRef.current = null;
    setGroupDragging(false);
    await Promise.all(startPositions.map(({ id, type, x, y }) => {
      const nx = x + currentDx;
      const ny = y + currentDy;
      if (type === "note") return handleSaveNotePosition(id, nx, ny);
      if (type === "sticker") return handleSaveStickerPosition(id, nx, ny);
      if (type === "section") return handleSaveSectionPosition(id, nx, ny);
      if (type === "shape") return handleSaveShapePosition(id, nx, ny);
      return handleSaveTextBoxPosition(id, nx, ny);
    }));
  };

  const { x: ox, y: oy, scale } = vp;
  const canvasCursor = tool === "text"
    ? "text"
    : (tool === "sticky" || (tool === "sticker" && activeStickerDef) || (tool === "shape" && activeShapeKind))
    ? "none"
    : (tool === "section" || selectionRect)
    ? "crosshair"
    : "default";

  return (
    <>
      {/* Combined floating bar: date + profile */}
      {(() => {
        const selectedDay = days.find(d => d.dateStr === selectedDate) ?? days[0];
        const otherDays = days.filter(d => d.dateStr !== selectedDate);
        const userInitial = (user.name || user.email)[0]?.toUpperCase() ?? "?";
        return (
          <div ref={floatingBarRef} style={{ position: "fixed", top: 12, left: 12, zIndex: 100, pointerEvents: "auto" }}>
            <div style={{ display: "flex", alignItems: "stretch", height: 44, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", overflow: "visible" }}>
              {/* Profile section */}
              <div
                onClick={() => { setProfileOpen(o => !o); setDateDropdownOpen(false); }}
                style={{ display: "flex", alignItems: "center", padding: "0 12px", cursor: "pointer", height: "100%" }}
              >
                <div style={{
                  width: 26, height: 26, borderRadius: "50%",
                  background: "var(--muted)", color: "var(--foreground)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 600, flexShrink: 0,
                }}>
                  {userInitial}
                </div>
              </div>

              {/* Divider */}
              <div style={{ width: 1, background: "var(--border)", margin: "8px 0", flexShrink: 0 }} />

              {/* Date section */}
              <div
                onClick={() => { setDateDropdownOpen(o => !o); setProfileOpen(false); }}
                style={{ display: "flex", alignItems: "center", padding: "0 14px", cursor: "pointer", height: "100%" }}
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <span style={{ fontSize: 9, lineHeight: 1, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.6 }}>{selectedDay?.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1, marginTop: 2 }}>{selectedDay?.date}</span>
                </div>
              </div>

              {/* Divider */}
              <div style={{ width: 1, background: "var(--border)", margin: "8px 0", flexShrink: 0 }} />

              {/* Inbox section */}
              <div
                onClick={() => { setInboxOpen(o => !o); setDateDropdownOpen(false); setProfileOpen(false); }}
                title="Inbox"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "0 12px", cursor: "pointer", height: "100%",
                  color: inboxOpen ? "var(--foreground)" : "var(--muted-foreground)",
                  position: "relative",
                }}
              >
                <InboxIcon size={18} />
                {(inboxNotes.length + inboxStickers.length) > 0 && (
                  <span style={{
                    position: "absolute", top: 6, right: 6,
                    minWidth: 14, height: 14, padding: "0 3px", borderRadius: 7,
                    background: "#0091ff", color: "#fff", fontSize: 9, fontWeight: 600,
                    display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
                  }}>{inboxNotes.length + inboxStickers.length}</span>
                )}
              </div>
            </div>

            {/* Date dropdown */}
            {dateDropdownOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 8px)", left: 0,
                background: "var(--card)", border: "1px solid var(--border)",
                borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                padding: 6, minWidth: 150, zIndex: 101,
              }}>
                {/* Week navigation */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 4px 6px", borderBottom: "1px solid var(--border)", marginBottom: 4 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setWeekOffset(w => w - 1); }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 4, fontSize: 14, color: "var(--foreground)", fontFamily: "inherit", lineHeight: 1 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "var(--muted)"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                  >‹</button>
                  <span style={{ fontSize: 11, fontWeight: 500, color: "var(--muted-foreground)", letterSpacing: "0.03em" }}>
                    {weekOffset === 0 ? "This week" : weekOffset === -1 ? "Last week" : weekOffset === 1 ? "Next week" : `Week ${weekOffset > 0 ? "+" : ""}${weekOffset}`}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setWeekOffset(w => w + 1); }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 4, fontSize: 14, color: "var(--foreground)", fontFamily: "inherit", lineHeight: 1 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "var(--muted)"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                  >›</button>
                </div>
                {days.map(day => {
                  const isSelected = day.dateStr === selectedDate;
                  return (
                    <div
                      key={day.dateStr}
                      onClick={() => { setVp(loadVp(day.dateStr)); setSelectedDate(day.dateStr); setDateDropdownOpen(false); }}
                      style={{
                        display: "flex", alignItems: "center", padding: "7px 10px", borderRadius: 6, cursor: "pointer", gap: 10,
                        background: isSelected ? "var(--foreground)" : "transparent",
                      }}
                      onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = "var(--muted)"; }}
                      onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
                    >
                      <span style={{ fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: isSelected ? "var(--background)" : "var(--muted-foreground)", width: 28 }}>{day.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: isSelected ? "var(--background)" : "var(--foreground)" }}>{day.date}</span>
                      {day.isToday && !isSelected && <span style={{ fontSize: 10, color: "var(--muted-foreground)", marginLeft: "auto" }}>today</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Profile dropdown */}
            {profileOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 8px)", left: 0,
                background: "var(--card)", border: "1px solid var(--border)",
                borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                padding: 6, minWidth: 180, zIndex: 101,
              }}>
                <div style={{ padding: "6px 10px 10px", borderBottom: "1px solid var(--border)", marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>{user.name || "Account"}</div>
                  <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 1 }}>{user.email}</div>
                </div>
                <button
                  onClick={() => { setProfileOpen(false); onSignOut(); }}
                  style={{ display: "block", width: "100%", padding: "6px 10px", background: "transparent", border: "none", borderRadius: 6, cursor: "pointer", textAlign: "left", fontSize: 13, fontFamily: "inherit", color: "var(--foreground)" }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "var(--muted)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                >
                  Sign out
                </button>
              </div>
            )}

            {/* Inbox sidenav */}
            {inboxOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 8px)", left: 0,
                background: "var(--card)", border: "1px solid var(--border)",
                borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                width: 240, maxHeight: "calc(100vh - 80px)",
                display: "flex", flexDirection: "column", zIndex: 101,
              }}>
                <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
                  <InboxIcon size={15} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>Inbox</span>
                  <button
                    onClick={() => setInboxOpen(false)}
                    title="Close inbox"
                    style={{
                      marginLeft: "auto", display: "flex", alignItems: "center", justifyContent: "center",
                      background: "transparent", border: "none", cursor: "pointer", padding: 2,
                      borderRadius: 6, color: "var(--muted-foreground)",
                    }}
                  >
                    <XIcon size={15} />
                  </button>
                </div>
                <div style={{ padding: 10, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                  {inboxNotes.length === 0 && inboxStickers.length === 0 ? (
                    <div style={{ padding: "20px 8px", textAlign: "center", fontSize: 12, color: "var(--muted-foreground)", lineHeight: 1.5 }}>
                      No notes in your inbox.<br />Notes added from mobile show up here.
                    </div>
                  ) : (
                    <>
                      {inboxNotes.map((note) => (
                        <div
                          key={note._id}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData("text/inbox-note", note._id); e.dataTransfer.effectAllowed = "move"; }}
                          title="Drag onto the canvas"
                          style={{
                            background: note.color, borderRadius: 8, padding: "12px 14px",
                            fontSize: 13, lineHeight: 1.55, color: "#1a1a1a", cursor: "grab",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                            whiteSpace: "pre-wrap", wordBreak: "break-word",
                            aspectRatio: "1 / 1", overflow: "hidden",
                          }}
                        >
                          {note.content || <span style={{ opacity: 0.45 }}>Empty note</span>}
                        </div>
                      ))}
                      {inboxStickers.map((inst) => (
                        <div
                          key={inst._id}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData("text/inbox-sticker", inst._id); e.dataTransfer.effectAllowed = "move"; }}
                          title="Drag onto the canvas"
                          style={{
                            borderRadius: 8, overflow: "hidden", cursor: "grab",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                            background: "var(--muted)",
                          }}
                        >
                          <img
                            src={`/api/sticker-images/${inst.stickerId}`}
                            alt="Photo from mobile"
                            style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }}
                          />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Pan/zoom canvas */}
      <div
        ref={canvasRef}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 0,
          cursor: canvasCursor,
          backgroundColor: "var(--background)",
          backgroundImage: [
            "radial-gradient(circle, var(--dot-major) 1.5px, transparent 1.5px)",
            "radial-gradient(circle, var(--dot-minor) 1px, transparent 1px)",
          ].join(", "),
          backgroundSize: [
            `${MAJOR * scale}px ${MAJOR * scale}px`,
            `${MINOR * scale}px ${MINOR * scale}px`,
          ].join(", "),
          backgroundPosition: [
            `${ox % (MAJOR * scale)}px ${oy % (MAJOR * scale)}px`,
            `${ox % (MINOR * scale)}px ${oy % (MINOR * scale)}px`,
          ].join(", "),
          userSelect: "none",
          overflow: "hidden",
        }}
        onMouseDown={onCanvasDown}
        onMouseMove={onCanvasMove}
        onMouseUp={onCanvasUp}
        onMouseLeave={() => { selectionRectRef.current = null; setSelectionRect(null); setGhostNotePos(null); setGhostStickerPos(null); setGhostSectionPos(null); setGhostShapePos(null); }}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
        onDrop={handleInboxDrop}
      >
        {((tool === "sticker" && activeStickerDef) || tool === "sticky" || (tool === "shape" && activeShapeKind)) && (
          <div
            style={{ position: "absolute", inset: 0, zIndex: 99, cursor: "crosshair" }}
            onMouseMove={onCanvasMove}
            onMouseUp={(e) => { e.stopPropagation(); onCanvasUp(e); }}
          />
        )}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            transformOrigin: "0 0",
            transform: `translate(${ox}px, ${oy}px) scale(${scale})`,
            pointerEvents: "none",
          }}
        >
          {tool === "sticky" && ghostNotePos && (
            <div
              style={{
                position: "absolute",
                left: ghostNotePos.x,
                top: ghostNotePos.y,
                width: NOTE_W,
                pointerEvents: "none",
                opacity: 0.55,
                borderRadius: 8,
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.12))",
                zIndex: 100,
              }}
            >
              <div style={{ background: selectedNoteColor, borderRadius: 8, padding: "8px 10px 10px", minHeight: NOTE_BODY_H + 18 }} />
            </div>
          )}
          {tool === "section" && ghostSectionPos && (() => {
            const sectionW = SECTION_PART_W * SECTION_PARTS;
            return (
              <div
                style={{
                  position: "absolute",
                  left: ghostSectionPos.x,
                  top: ghostSectionPos.y,
                  width: sectionW,
                  pointerEvents: "none",
                  opacity: 0.5,
                  zIndex: 100,
                }}
              >
                <div style={{ height: SECTION_TITLE_H, display: "flex", alignItems: "center", paddingLeft: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--muted-foreground)" }}>Untitled</span>
                </div>
                <div style={{ display: "flex", height: SECTION_H, border: "1.5px dashed var(--border)", borderRadius: 16, overflow: "hidden", background: "var(--muted)" }}>
                  {Array.from({ length: SECTION_PARTS }, (_, i) => (
                    <div key={i} style={{ flex: 1, borderLeft: i > 0 ? "1px dashed var(--border)" : "none" }} />
                  ))}
                </div>
              </div>
            );
          })()}
          {tool === "sticker" && activeStickerDef && ghostStickerPos && (
            <div
              style={{
                position: "absolute",
                left: ghostStickerPos.x,
                top: ghostStickerPos.y,
                width: STICKER_SIZE,
                height: STICKER_SIZE,
                pointerEvents: "none",
                opacity: 0.65,
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.12))",
                zIndex: 100,
              }}
            >
              <img
                src={`/api/sticker-images/${activeStickerDef._id}`}
                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                draggable={false}
                alt=""
              />
            </div>
          )}
          {tool === "shape" && activeShapeKind && ghostShapePos && (() => {
            const { w, h } = shapeDefaultSize(activeShapeKind);
            return (
              <div
                style={{
                  position: "absolute",
                  left: ghostShapePos.x,
                  top: ghostShapePos.y,
                  width: w,
                  height: h,
                  pointerEvents: "none",
                  opacity: 0.6,
                  filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.12))",
                  zIndex: 100,
                }}
              >
                <ShapeGlyph kind={activeShapeKind} color={selectedShapeColor} width="100%" height="100%" />
              </div>
            );
          })()}
          {sections.map((section) => (
            <SectionCard
              key={section._id}
              section={section}
              scaleRef={scaleRef}
              selected={selectedIds.includes(section._id)}
              onSelect={(id) => setSelectedIds([id])}
              onDragStart={handleSectionDragStart}
              onMove={handleMoveSection}
              onSavePosition={handleSaveSectionPosition}
              onTitleChange={handleSectionTitleChange}
              onResize={handleResizeSection}
              onSaveSize={handleSaveSectionSize}
            />
          ))}
          {shapes.map((shape) => (
            <ShapeCard
              key={shape._id}
              shape={shape}
              scaleRef={scaleRef}
              selected={selectedIds.includes(shape._id)}
              onSelect={(id) => setSelectedIds([id])}
              onMove={handleMoveShape}
              onSavePosition={handleSaveShapePosition}
              onResize={handleResizeShape}
              onSaveSize={handleSaveShapeSize}
              onColorChange={handleShapeColorChange}
            />
          ))}
          {stickerInstances.map((inst) => (
            <StickerInstanceCard
              key={inst._id}
              instance={inst}
              scaleRef={scaleRef}
              selected={selectedIds.includes(inst._id)}
              onSelect={(id) => setSelectedIds([id])}
              onMove={handleMoveStickerInstance}
              onSavePosition={handleSaveStickerPosition}
              onResize={handleResizeStickerInstance}
              onSaveSize={handleSaveStickerSize}
            />
          ))}
          {notes.map((note) => (
            <StickyNoteCard
              key={note._id}
              note={note}
              scaleRef={scaleRef}
              selected={selectedIds.includes(note._id)}
              autoFocus={note._id === newNoteId}
              onSelect={(id) => setSelectedIds([id])}
              onDragStart={handleNoteDragStart}
              onMove={handleMoveNote}
              onSavePosition={handleSaveNotePosition}
              onContentChange={handleContentChange}
              onColorChange={handleColorChange}
            />
          ))}
          {textBoxes.map((box) => (
            <TextBoxCard
              key={box._id}
              box={box}
              scaleRef={scaleRef}
              selected={selectedIds.includes(box._id)}
              autoFocus={box._id === newTextBoxId}
              onSelect={(id) => setSelectedIds([id])}
              onMove={handleMoveTextBox}
              onSavePosition={handleSaveTextBoxPosition}
              onContentChange={handleTextBoxContentChange}
            />
          ))}
          {(() => {
            if (selectedIds.length < 2) return null;
            const PADDING = 10;
            const rects = [
              ...sections.filter((s) => selectedIds.includes(s._id)).map((s) => ({ x: s.x, y: s.y, r: s.x + s.w, b: s.y + s.h + SECTION_TITLE_H })),
              ...notes.filter((n) => selectedIds.includes(n._id)).map((n) => ({ x: n.x, y: n.y, r: n.x + NOTE_W, b: n.y + NOTE_BODY_H + NOTE_HEADER_H })),
              ...stickerInstances.filter((s) => selectedIds.includes(s._id)).map((s) => ({ x: s.x, y: s.y, r: s.x + s.w, b: s.y + s.h })),
              ...textBoxes.filter((t) => selectedIds.includes(t._id)).map((t) => ({ x: t.x, y: t.y, r: t.x + 300, b: t.y + 80 })),
              ...shapes.filter((s) => selectedIds.includes(s._id)).map((s) => ({ x: s.x, y: s.y, r: s.x + s.w, b: s.y + s.h })),
            ];
            if (rects.length === 0) return null;
            const gx = Math.min(...rects.map((r) => r.x)) - PADDING;
            const gy = Math.min(...rects.map((r) => r.y)) - PADDING;
            const gr = Math.max(...rects.map((r) => r.r)) + PADDING;
            const gb = Math.max(...rects.map((r) => r.b)) + PADDING;
            return (
              <div
                style={{
                  position: "absolute",
                  left: gx,
                  top: gy,
                  width: gr - gx,
                  height: gb - gy,
                  border: "1.5px solid #0091ff",
                  borderRadius: 4,
                  background: "rgba(0, 145, 255, 0.04)",
                  pointerEvents: "auto",
                  cursor: groupDragging ? "grabbing" : "grab",
                  zIndex: 20,
                  boxSizing: "border-box",
                }}
                onPointerDown={onGroupPointerDown}
                onPointerMove={onGroupPointerMove}
                onPointerUp={onGroupPointerUp}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
              />
            );
          })()}
        </div>
      </div>

      {selectionRect && (
        <div
          style={{
            position: "fixed",
            left: Math.min(selectionRect.sx, selectionRect.ex),
            top: Math.min(selectionRect.sy, selectionRect.ey),
            width: Math.abs(selectionRect.ex - selectionRect.sx),
            height: Math.abs(selectionRect.ey - selectionRect.sy),
            border: "1.5px solid #0091ff",
            background: "rgba(0, 145, 255, 0.08)",
            borderRadius: 2,
            pointerEvents: "none",
            zIndex: 14,
          }}
        />
      )}

      {/* Figma-style floating toolbar */}
      <div
        style={{
          position: "fixed",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 20,
        }}
      >
        {stickerPanelOpen && (
          <StickerPanel
            stickers={stickerDefs}
            onStickerSelect={(s) => {
              setActiveStickerDef(s);
              setTool("sticker");
              setStickerPanelOpen(false);
            }}
            onUpload={handleUploadSticker}
            onUploadUrl={handleUploadStickerFromUrl}
            onDelete={handleDeleteStickerDef}
          />
        )}

        {shapePanelOpen && (
          <ShapePanel
            activeKind={tool === "shape" ? activeShapeKind : null}
            onSelect={(kind) => {
              setActiveShapeKind(kind);
              setTool("shape");
              setShapePanelOpen(false);
              setStickerPanelOpen(false);
              setActiveStickerDef(null);
            }}
          />
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            padding: "4px 6px",
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <ToolButton
            active={tool === "pan" && !stickerPanelOpen && !shapePanelOpen}
            onClick={() => { setTool("pan"); setStickerPanelOpen(false); setActiveStickerDef(null); setShapePanelOpen(false); setActiveShapeKind(null); }}
            title="Move (V)"
          >
            <MousePointer2 size={17} />
          </ToolButton>
          <ToolButton
            active={tool === "text"}
            onClick={() => { setTool("text"); setStickerPanelOpen(false); setShapePanelOpen(false); setActiveShapeKind(null); }}
            title="Text (T)"
          >
            <TypeIcon size={17} />
          </ToolButton>
          <ToolButton
            active={tool === "sticky"}
            onClick={() => { setTool("sticky"); setStickerPanelOpen(false); setShapePanelOpen(false); setActiveShapeKind(null); }}
            title="Sticky Note (S)"
          >
            <StickyNoteIcon size={17} />
          </ToolButton>
          <ToolButton
            active={stickerPanelOpen || tool === "sticker"}
            onClick={() => {
              if (tool === "sticker") { setTool("pan"); setActiveStickerDef(null); }
              setShapePanelOpen(false);
              setActiveShapeKind(null);
              setStickerPanelOpen((o) => !o);
            }}
            title="Stickers"
          >
            <ImageIcon size={17} />
          </ToolButton>
          <ToolButton
            active={shapePanelOpen || tool === "shape"}
            onClick={() => {
              if (tool === "shape") { setTool("pan"); setActiveShapeKind(null); }
              setStickerPanelOpen(false);
              setActiveStickerDef(null);
              setShapePanelOpen((o) => !o);
            }}
            title="Shapes"
          >
            <ShapesIcon size={17} />
          </ToolButton>
          <ToolButton
            active={tool === "section"}
            onClick={() => { setTool("section"); setStickerPanelOpen(false); setActiveStickerDef(null); setShapePanelOpen(false); setActiveShapeKind(null); }}
            title="Section (G)"
          >
            <Columns3 size={17} />
          </ToolButton>
        </div>
      </div>
    </>
  );
}

export default App;
